/**
 * State Intel (spec sections 46-49, 57).
 *
 * Rival intelligence is the easiest place in this product to start asserting
 * things. Everything here is either a plain observation from Jeabs or an
 * explicitly-labelled detection; there are no rival "threat scores", because we
 * have no observations of rival players' actual operations to build one from.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlayers, useStateAlliances, useStateTransfers } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import { PageHeader, Value } from '../../components/ui/primitives';
import { DataTable } from '../../components/ui/DataTable';
import {
  EmptyState,
  ErrorState,
  LoadingBlock,
  NotAvailableYet,
  QueryBoundary,
} from '../../components/ui/states';
import { FreshnessBadge } from '../../components/freshness/Freshness';
import { formatCompact, formatNumber } from '../../lib/format';
import { usePlayerColumns } from '../players/playerColumns';
import { DetectedEventTable } from '../alliance/AlliancePages';
import { useT } from '../../i18n';

const TRANSFER_WINDOWS = [7, 14, 30] as const;

function NoKingdom({ title }: { title: string }) {
  const t = useT();
  return (
    <>
      <PageHeader title={title} />
      <EmptyState
        title={t.stateIntel.noKingdomTitle}
        body={t.stateIntel.noKingdomBody}
        action={
          <Link className="btn btn--sm" to="/data/sync">
            {t.common.openSyncStatus}
          </Link>
        }
      />
    </>
  );
}

export function StateAlliancesPage() {
  const t = useT();
  const core = useCoreContext();
  const alliances = useStateAlliances(core.kingdomId ?? undefined);
  const columns = t.stateIntel.alliances.columns;

  if (core.isPending) return <LoadingBlock label={t.common.loadingState} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (core.kingdomId === null) return <NoKingdom title={t.stateIntel.alliances.title} />;

  const kingdomId = core.kingdomId;

  return (
    <>
      <PageHeader
        title={t.stateIntel.alliances.title}
        subtitle={t.stateIntel.alliances.subtitle(kingdomId)}
      />

      <QueryBoundary
        query={alliances}
        loadingLabel={t.stateIntel.alliances.loading}
        isEmpty={(data) => data.alliances.length === 0}
        empty={
          <EmptyState
            title={t.stateIntel.alliances.emptyTitle}
            body={t.stateIntel.alliances.emptyBody}
          />
        }
      >
        {(data) => (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{columns.alliance}</th>
                  <th className="num">{columns.level}</th>
                  <th className="num">{columns.power}</th>
                  <th className="num">{columns.kills}</th>
                  <th className="num">{columns.members}</th>
                  <th>{columns.leader}</th>
                  <th>{columns.age}</th>
                </tr>
              </thead>
              <tbody>
                {[...data.alliances]
                  .sort((a, b) => (b.power ?? -1) - (a.power ?? -1))
                  .map((alliance) => (
                    <tr key={alliance.id}>
                      <td>
                        {alliance.abbr ? <strong>[{alliance.abbr}] </strong> : null}
                        <Value absent={t.common.unnamedAlliance}>{alliance.name}</Value>
                        {alliance.id === core.allianceId ? (
                          <span className="badge badge--accent" style={{ marginLeft: 6 }}>
                            {t.stateIntel.alliances.ours}
                          </span>
                        ) : null}
                      </td>
                      <td className="num">
                        <Value absent={t.common.dash}>{alliance.level}</Value>
                      </td>
                      <td className="num">
                        <Value absent={t.common.unknown}>{formatCompact(alliance.power)}</Value>
                      </td>
                      <td className="num">
                        <Value absent={t.common.unknown}>{formatCompact(alliance.kills)}</Value>
                      </td>
                      <td className="num">
                        <Value absent={t.common.unknown}>
                          {alliance.memberCount === null
                            ? null
                            : t.overview.memberCount(
                                formatNumber(alliance.memberCount) ?? '',
                                alliance.memberMax ? formatNumber(alliance.memberMax) : null,
                              )}
                        </Value>
                      </td>
                      <td>
                        <Value absent={t.common.unknown}>{alliance.leaderName}</Value>
                      </td>
                      <td>
                        <FreshnessBadge label="" info={alliance.freshness} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

export function StatePlayersPage() {
  const t = useT();
  const [search, setSearch] = useState('');
  const players = usePlayers({ search: search.trim() || undefined, limit: 200 });
  const columns = usePlayerColumns({ showAlliance: true });

  return (
    <>
      <PageHeader
        title={t.stateIntel.players.title}
        subtitle={t.stateIntel.players.subtitle}
        actions={
          <input
            type="search"
            placeholder={t.stateIntel.players.searchPlaceholder}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label={t.stateIntel.players.searchAria}
            style={{ width: 220 }}
          />
        }
      />

      <QueryBoundary
        query={players}
        loadingLabel={t.stateIntel.players.loading}
        isEmpty={(data) => data.players.length === 0}
        empty={
          <EmptyState
            title={search ? t.stateIntel.players.noMatchTitle : t.stateIntel.players.emptyTitle}
            body={search ? t.stateIntel.players.noMatchBody : t.stateIntel.players.emptyBody}
          />
        }
      >
        {(data) => (
          <>
            <p className="muted small">
              {t.stateIntel.players.showing(data.players.length, formatNumber(data.total) ?? '')}
            </p>
            <DataTable
              data={data.players}
              columns={columns}
              initialSorting={[{ id: 'power', desc: true }]}
              emptyMessage={t.stateIntel.players.noMatch}
            />
          </>
        )}
      </QueryBoundary>
    </>
  );
}

export function StateTransfersPage() {
  const t = useT();
  const core = useCoreContext();
  const [days, setDays] = useState(14);
  const transfers = useStateTransfers(core.kingdomId ?? undefined, days);

  if (core.isPending) return <LoadingBlock label={t.common.loadingState} />;
  if (core.error) return <ErrorState error={core.error} />;
  if (core.kingdomId === null) return <NoKingdom title={t.stateIntel.transfers.title} />;

  return (
    <>
      <PageHeader
        title={t.stateIntel.transfers.title}
        subtitle={t.stateIntel.transfers.subtitle}
        actions={
          <div className="row" style={{ gap: 4 }}>
            {TRANSFER_WINDOWS.map((option) => (
              <button
                key={option}
                type="button"
                className={days === option ? 'btn btn--sm btn--primary' : 'btn btn--sm'}
                onClick={() => setDays(option)}
              >
                {t.common.days(option)}
              </button>
            ))}
          </div>
        }
      />

      <QueryBoundary
        query={transfers}
        loadingLabel={t.stateIntel.transfers.loading}
        isEmpty={(data) => data.transfers.length === 0}
        empty={
          <EmptyState
            title={t.stateIntel.transfers.emptyTitle}
            body={t.stateIntel.transfers.emptyBody}
          />
        }
      >
        {(data) => <DetectedEventTable events={data.transfers} />}
      </QueryBoundary>
    </>
  );
}

export function StateLeaderboardsPage() {
  const t = useT();
  return (
    <>
      <PageHeader
        title={t.stateIntel.leaderboards.title}
        subtitle={t.stateIntel.leaderboards.subtitle}
      />
      <NotAvailableYet
        feature={t.stateIntel.leaderboards.feature}
        phase={t.stateIntel.leaderboards.phase}
        describes={t.stateIntel.leaderboards.describes}
      />
    </>
  );
}

export function StateKvkPage() {
  const t = useT();
  return (
    <>
      <PageHeader title={t.stateIntel.kvk.title} subtitle={t.stateIntel.kvk.subtitle} />
      <NotAvailableYet
        feature={t.stateIntel.kvk.feature}
        phase={t.stateIntel.kvk.phase}
        describes={t.stateIntel.kvk.describes}
      />
    </>
  );
}
